/**
 * Sentry — Frontend Error Monitoring
 *
 * Initializes Sentry for production error tracking.
 * PII is disabled for HIPAA compliance.
 *
 * Set VITE_SENTRY_DSN in your environment to activate.
 */
import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined

export function initSentry() {
  if (!SENTRY_DSN) return

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    // Performance monitoring — sample 10% of transactions
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    // HIPAA: never send PII
    sendDefaultPii: false,
    // Only report errors in production
    enabled: import.meta.env.PROD,
    // Ignore common noise
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Network Error',
      'AbortError',
    ],
    beforeSend(event) {
      // Strip any potential PHI from breadcrumbs & error messages
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(bc => {
          // Remove URL query params that might contain patient data
          if (bc.data?.url) {
            try {
              const url = new URL(bc.data.url, window.location.origin)
              url.search = ''
              bc.data.url = url.toString()
            } catch { /* leave as-is */ }
          }
          return bc
        })
      }
      return event
    },
  })
}

/** Manually capture an error to Sentry */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!SENTRY_DSN) return
  Sentry.captureException(error, { extra: context })
}

export { Sentry }
